import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/dateUtils";
import { addSingaporeCalendarDays, getSingaporeDateKeyFromInstant } from "@shared/timezone";
import { formatElapsedSeconds, stopReasonLabel } from "@shared/roWorkTimer";
import { Link } from "wouter";
import { useMemo, useState } from "react";
import { AdminRecoverControl } from "./FloatingRoWorkTimers";
import { useLiveElapsed } from "./useLiveElapsed";

export function WorkInProgressPanel() {
  const { user } = useAuth();
  const wip = trpc.roWorkSessions.wip.useQuery(undefined, { refetchInterval: 30_000 });
  const today = getSingaporeDateKeyFromInstant(new Date()) ?? "";
  const [date, setDate] = useState(today);
  const history = trpc.roWorkSessions.history.useQuery({ date }, { enabled: Boolean(date) });
  const running = wip.data?.running ?? [];
  const paused = wip.data?.paused ?? [];

  return (
    <section className="space-y-4 rounded-lg border bg-white p-4">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide">Work in progress</h2>
        <p className="text-xs text-muted-foreground">Running and recently paused repair orders. Times are Singapore time.</p>
      </div>
      {running.length === 0 && paused.length === 0 ? (
        <p className="text-sm text-muted-foreground">No active or recently paused repair work.</p>
      ) : null}
      <div className="space-y-2">
        {running.map((session) => (
          <SessionLine key={session.sessionId} session={session} live />
        ))}
        {paused.map((session) => (
          <SessionLine key={session.sessionId} session={session} />
        ))}
      </div>
      {user?.role === "admin"
        ? running.map((session) => (
            <div key={`recover-${session.sessionId}`}>
              <p className="text-xs text-muted-foreground">Recover {session.jobNumber}</p>
              <AdminRecoverControl sessionId={session.sessionId} />
            </div>
          ))
        : null}
      <div className="border-t pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">History</h3>
          <button type="button" className="rounded-full border px-3 py-1 text-xs" onClick={() => setDate(today)}>Today</button>
          <button type="button" className="rounded-full border px-3 py-1 text-xs" onClick={() => setDate(addSingaporeCalendarDays(today, -1))}>Yesterday</button>
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="rounded-md border px-2 py-1 text-sm" />
        </div>
        <HistoryDay date={date} sessions={history.data?.sessions ?? []} totalSeconds={history.data?.totalSeconds ?? 0} />
      </div>
    </section>
  );
}

function SessionLine({
  session,
  live = false,
}: {
  live?: boolean;
  session: {
    sessionId: number;
    repairOrderId: number;
    jobNumber: string;
    equipment: string;
    customerName: string;
    technicianName: string;
    startedAt: Date | string;
    stoppedAt: Date | string | null;
    elapsedSeconds: number;
    stopReason: string | null;
    stopSource: string | null;
    stopNotes: string | null;
    status: string;
  };
}) {
  const liveElapsed = useLiveElapsed(session.startedAt, live ? null : session.stoppedAt);
  const elapsed = live ? liveElapsed : formatElapsedSeconds(session.elapsedSeconds);
  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link href={`/repair-orders/id/${session.repairOrderId}`} className="font-semibold underline">{session.jobNumber}</Link>
        <span className="font-mono">{elapsed}</span>
      </div>
      <p>{session.equipment} · {session.customerName}</p>
      <p className="text-xs text-muted-foreground">{session.technicianName} · {session.status.replace(/_/g, " ")}</p>
      <p className="text-xs text-muted-foreground">Start {formatDateTime(session.startedAt)}{session.stoppedAt ? ` · Stop ${formatDateTime(session.stoppedAt)}` : " · Running"}</p>
      {session.stoppedAt ? (
        <p className="text-xs">{stopReasonLabel(session.stopReason, session.stopSource)}{session.stopNotes ? ` — ${session.stopNotes}` : ""}</p>
      ) : null}
    </div>
  );
}

function HistoryDay({
  date,
  sessions,
  totalSeconds,
}: {
  date: string;
  totalSeconds: number;
  sessions: Array<{
    sessionId: number;
    repairOrderId: number;
    jobNumber: string;
    equipment: string;
    customerName: string;
    technicianName: string;
    startedAt: Date | string;
    stoppedAt: Date | string | null;
    secondsOnThisDay: number;
    fullDurationSeconds: number;
    crossesMidnight: boolean;
    stopReason: string | null;
    stopSource: string | null;
  }>;
}) {
  const label = useMemo(() => date, [date]);
  if (sessions.length === 0) return <p className="mt-2 text-sm text-muted-foreground">No repair work recorded for {label}.</p>;
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-muted-foreground">Time on {label}: {formatElapsedSeconds(totalSeconds)}. A session that crosses midnight is split by calendar day so the total is not counted twice.</p>
      {sessions.map((session) => (
        <div key={`${label}-${session.sessionId}`} className="rounded-md border p-3 text-sm">
          <Link href={`/repair-orders/id/${session.repairOrderId}`} className="font-semibold underline">{session.jobNumber}</Link>
          <p>{session.equipment} · {session.customerName} · {session.technicianName}</p>
          <p className="text-xs text-muted-foreground">Start {formatDateTime(session.startedAt)} · Stop {session.stoppedAt ? formatDateTime(session.stoppedAt) : "Still running"}</p>
          <p className="text-xs">This day {formatElapsedSeconds(session.secondsOnThisDay)} · Session {formatElapsedSeconds(session.fullDurationSeconds)}{session.crossesMidnight ? " · crosses midnight" : ""}</p>
          <p className="text-xs">{stopReasonLabel(session.stopReason, session.stopSource)}</p>
        </div>
      ))}
    </div>
  );
}
